import React, { useContext, useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import "./modern-style.css";
import "../../../styles/page-titles.css";
import "react-toastify/dist/ReactToastify.css";

import { formatDateTz } from "../../../utils/timezone";
import { IoAddCircleOutline, IoWalletOutline } from "react-icons/io5";
import { IoMdLock } from "react-icons/io";
import { SlRefresh } from "react-icons/sl";
import { FiSearch, FiActivity } from "react-icons/fi";
import { HiOutlineReceiptTax, HiOutlineCash } from "react-icons/hi";
import { BsReceipt } from "react-icons/bs";

import SangriaModal from "../../../components/Caixa/Modals/SangriaModal";
import { Modal } from "../../../components/Modal";
import SuprimentoModal from "../../../components/Caixa/Modals/SuprimentoModal";
import CloseCaixaModal from "../../../components/Caixa/Modals/CloseCaixaModal";
import { CaixaContext } from "../../../contexts/caixaContext";
import { formatPrice } from "../../../utils/formatPrice";
import { formatPriceWithSymbol } from "../../../utils/currency";
import { exibirSentinela } from "../../../utils/sentinelas";

import { TbClockHour4 } from "react-icons/tb";
import { useNavigate } from "react-router-dom";

import { getPedido } from "../../../services/api";
import { AuthContext } from "../../../contexts/auth";
import ModalPedido from "../../../components/ModalPedido";
import { toast } from "react-toastify";
import styled from "styled-components";
import CustomTable from '../../../components/CustomTable';

import { SidebarContext } from "../../../AppRoutes"

const ContainerCaixa = styled.div`
  width: auto;
  min-height: 100vh;
  background: #ffffff;
  margin-left: ${({ sidebar }) => (sidebar ? '250px' : '100px')};
  padding: 24px;
  transition: margin-left 200ms ease;
  display: flex;
  flex-direction: column;

  @media (max-width: 880px) {
    margin-left: 0;
    padding: 16px;
  }
`

function OpenedCaixa() {
  const { t } = useTranslation('cashRegister');
  const [currentModal, setCurrentModal] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showPedido, setShowPedido] = useState(true);
  const [infoPedido, setInfoPedido] = useState({});


  const { sidebar } = useContext(SidebarContext);

  const empresa = localStorage.getItem('empresa')
  const empresaParse = JSON.parse(empresa)
  const vinculo_empresa = empresaParse.cnpj;

  const { user } = useContext(AuthContext);

  const navigate = useNavigate();

  const {
    caixa,
    sangriasValue,
    suprimentosValue,
    caixaValue,
    isLoading,
    refetch,
    dinheiroValue,
    cartaoValue,
    pixValue,
  } = useContext(CaixaContext);

  const handleShowPedido = async (pedidoId) => {
    setShowPedido(!showPedido);
    try {
      const response = await getPedido(
        user._id,
        caixa.id_empresa,
        vinculo_empresa,
        pedidoId
      );
      if (showPedido) {
        if (response.data.pedido) {
          setInfoPedido(response.data.pedido);
        }
      }
    } catch (e) {
      process.env.REACT_APP_ISDEVELOP === 'true' && console.log("error", e);
      if (e?.response?.data?.msg) {
        toast(e?.response.data.msg, { type: "error" });
      } else {
        toast(t('opened.fetchError'), { type: "error" });
      }
    }
  };

  const onCloseModal = () => {
    setCurrentModal(null);
  };

  const modalContent = {
    sangria: () => <SangriaModal onClose={onCloseModal} />,
    suprimento: () => <SuprimentoModal onClose={onCloseModal} />,
    closeCaixa: () => <CloseCaixaModal onClose={onCloseModal} />,
  };

  const columns = [
    {
      title: t('transactions.columns.dateTime'),
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: t('transactions.columns.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('transactions.columns.value'),
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: t('transactions.columns.paymentMethod'),
      dataIndex: 'payment_method',
      key: 'payment_method',
      responsive: ['lg'],
    },
  ];

  const tableData = useMemo(() => {
    if(!caixa?.caixa_items) return [];

    let filteredData = caixa.caixa_items;

    // Aplicar filtro de busca se houver termo de pesquisa
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredData = caixa.caixa_items.filter((item) => {
        const description = item.descricao?.toLowerCase() || "";
        const paymentMethod = item.tipo_lancamento?.toLowerCase() || "";
        const value = formatPrice(Number(item.valor || 0)).toLowerCase();
        const date = formatDateTz(item.createdAt).toLowerCase();

        return description.includes(searchLower) ||
               paymentMethod.includes(searchLower) ||
               value.includes(searchLower) ||
               date.includes(searchLower);
      });
    }

    return filteredData.map((caixa) => {
      return {
        key: caixa._id,
        created_at: formatDateTz(caixa.createdAt),
        // descricao e tipo_lancamento sao gravados pelo backend em portugues
        // ("Saldo Inicial", "Dinheiro") e usados por ELE como sentinela de
        // logica -- caixaV2 decide o que e venda comparando contra a string.
        // Por isso a traducao acontece aqui, na exibicao, e nao na gravacao.
        description: caixa.tipo_operacao
          ? `${exibirSentinela(caixa.descricao)} (${exibirSentinela(caixa.tipo_operacao)})`
          : exibirSentinela(caixa.descricao),
        value: formatPriceWithSymbol(Number(caixa.valor || 0)),
        payment_method: exibirSentinela(caixa.tipo_lancamento),
        id_pedido: caixa.id_pedido
      }
    })
  }, [caixa, searchTerm])

  return (
    <>
      <ContainerCaixa sidebar={sidebar}>
        <div className="modern-caixa-container">
          {/* Header Section */}
          <div className="modern-caixa-header">
            <div className="modern-caixa-title">
              <div className="page-title-with-icon">
                <div className="page-icon"><IoWalletOutline /></div>
                <h1 className="page-title">
                  {t('opened.title')}
                  <span className="status-badge">
                    <FiActivity />
                    {t('opened.active')}
                  </span>
                </h1>
              </div>
            </div>

            {/* Controls Section */}
            <div className="modern-caixa-controls">
              <div className="modern-search-container">
                <FiSearch className="modern-search-icon" />
                <input
                  className="modern-search-input"
                  id="search"
                  placeholder={t('opened.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="modern-actions-container">
                <button
                  type="button"
                  className="modern-btn modern-btn-danger"
                  onClick={() => setCurrentModal("closeCaixa")}
                >
                  <IoMdLock />
                  {t('opened.closeRegister')}
                </button>
                <button
                  type="button"
                  className="modern-btn modern-btn-primary"
                  onClick={() => setCurrentModal("sangria")}
                >
                  <HiOutlineCash />
                  {t('opened.withdrawal')}
                </button>
                <button
                  type="button"
                  className="modern-btn modern-btn-primary"
                  onClick={() => setCurrentModal("suprimento")}
                >
                  <IoAddCircleOutline />
                  {t('opened.supply')}
                </button>
                <button
                  className="modern-btn modern-btn-secondary"
                  onClick={() => {
                    process.env.REACT_APP_ISDEVELOP === 'true' && console.log('Atualizando dados do caixa...');
                    refetch();
                  }}
                >
                  <SlRefresh />
                  {t('opened.refresh')}
                </button>
                <button
                  className="modern-btn modern-btn-outline"
                  onClick={() => navigate("/caixa/historico")}
                >
                  <TbClockHour4 />
                  {t('opened.history')}
                </button>
              </div>
            </div>
          </div>
          {/* Main Content */}
          {isLoading ? (
            <div className="modern-loading">
              <div className="modern-loading-spinner"></div>
              <div className="modern-loading-text">{t('opened.loading')}</div>
            </div>
          ) : (
            <div className="modern-caixa-content">
              {/* Transactions Table */}
              <div className="modern-transactions-container">
                <div className="modern-table-header">
                  <BsReceipt />
                  <h3>{t('transactions.title')}</h3>
                  <span className="modern-table-count">
                    {t('transactions.count', { count: tableData.length })}
                  </span>
                </div>
                <div className="modern-table-wrapper">
                                          <CustomTable
                    className="modern-transactions-table"
                    columns={columns}
                    dataSource={tableData}
                    pagination={false}
                    rowClassName={(record) => record.id_pedido ? 'pedido-row' : ''}
                    rowHoverable={true}
                    onRow={(record) => ({
                      onClick: () => record.id_pedido && handleShowPedido(record.id_pedido)
                    })}
                    locale={{
                      emptyText: (
                        <div className="modern-empty-state">
                          <HiOutlineReceiptTax className="modern-empty-icon" />
                          <div className="modern-empty-title">
                            {t('transactions.emptyTitle')}
                          </div>
                          <div className="modern-empty-description">
                            {searchTerm.trim()
                              ? t('transactions.emptySearchDescription', { searchTerm })
                              : t('transactions.emptyDescription')
                            }
                          </div>
                        </div>
                      )
                    }}
                  />
                </div>
              </div>

              {/* Summary Card */}
              <div className="modern-summary-card">
                <div className="openedCaixa-summary-header modern-summary-header">
                  <h3>{t('summary.title')}</h3>
                </div>
                <div className="modern-summary-content">
                  {/* Caixa Info */}
                  <div className="modern-caixa-info">
                    <div className="modern-caixa-info-item">
                      <div className="modern-caixa-info-label">{t('summary.identifier')}</div>
                      <div className="modern-caixa-info-value">{caixa._id}</div>
                    </div>
                    <div className="modern-caixa-info-item">
                      <div className="modern-caixa-info-label">{t('summary.openedAt')}</div>
                      <div className="modern-caixa-info-value">
                        {formatDateTz(caixa.createdAt)}
                      </div>
                    </div>
                  </div>

                  {/* Resumo do Caixa */}
                  <div className="modern-summary-section">
                    <h4 className="modern-summary-section-title">{t('summary.cashSummary')}</h4>
                    <div className="modern-summary-item">
                      <span className="modern-summary-label">{t('summary.initialBalance')}</span>
                      <span className="modern-summary-value">{formatPriceWithSymbol(caixa.saldo_inicial)}</span>
                    </div>
                    <div className="modern-summary-item">
                      <span className="modern-summary-label">{t('summary.entries')}</span>
                      <span className="modern-summary-value positive">{formatPrice(suprimentosValue)}</span>
                    </div>
                    <div className="modern-summary-item">
                      <span className="modern-summary-label">{t('summary.withdrawals')}</span>
                      <span className="modern-summary-value negative">{formatPrice(Math.abs(sangriasValue))}</span>
                    </div>
                  </div>

                  {/* Valores por Forma de Pagamento */}
                  <div className="modern-summary-section">
                    <h4 className="modern-summary-section-title">{t('summary.cashValues')}</h4>
                    <div className="modern-summary-item">
                      <span className="modern-summary-label">💰 {t('payment.cash')}</span>
                      <span className="modern-summary-value">{formatPriceWithSymbol(dinheiroValue)}</span>
                    </div>
                    <div className="modern-summary-item">
                      <span className="modern-summary-label">💳 {t('payment.card')}</span>
                      <span className="modern-summary-value">{formatPriceWithSymbol(cartaoValue)}</span>
                    </div>
                    <div className="modern-summary-item">
                      <span className="modern-summary-label">📱 {t('payment.pix')}</span>
                      <span className="modern-summary-value">{formatPriceWithSymbol(pixValue)}</span>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="modern-summary-total">
                    <div className="modern-summary-label">{t('summary.totalInRegister')}</div>
                    <div className="modern-summary-value">{formatPriceWithSymbol(caixaValue)}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ContainerCaixa>
      {currentModal && (
        <Modal isOpen={!!currentModal} onClose={() => setCurrentModal(null)}>
          {modalContent[currentModal]()}
        </Modal>
      )}
      {infoPedido && (
        <ModalPedido
          showPedido={showPedido}
          setShowPedido={setShowPedido}
          infoPedido={infoPedido}
          showActions={false}
        />
      )}
    </>
  );
}

export default OpenedCaixa;
